import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useStore } from '../context/StoreContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loginUser, logoutUser } = useStore();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleSubscribe = () => {
    window.open('https://pages.razorpay.com/rcktalgonewplan', '_blank');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Courses', path: '/courses' },
    { name: 'Basics of RocketAlgo', path: '/basics' },
  ];

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-500 ease-in-out ${scrolled ? 'bg-[#141414]/90 backdrop-blur-xl shadow-lg shadow-black/50 py-2' : 'bg-gradient-to-b from-black/80 via-black/40 to-transparent py-4 md:py-6'}`}>
        <div className="container mx-auto px-4 md:px-12 flex items-center justify-between h-14">
          
          <div className="flex items-center space-x-12 h-full">
            <Link to="/" className="flex items-center">
              <span className="text-[20px] md:text-[24px] font-black tracking-wider uppercase text-white">
                ROCKETALGO<span className="text-rocket">.TV</span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center space-x-8 h-full">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path || (link.name === 'Home' && location.pathname === '/');
                return (
                  <Link 
                    key={link.name} 
                    to={link.path} 
                    className={`relative h-full flex items-center text-[14px] transition-all duration-300 ${isActive ? 'text-white font-bold' : 'text-gray-300 hover:text-white font-medium'}`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center space-x-4 md:space-x-6">
            <Link to="/live" className="hidden sm:flex items-center space-x-2 border border-danger/50 bg-danger/10 hover:bg-danger/20 transition-colors px-3 py-1.5 rounded-full text-white text-xs font-bold backdrop-blur-md">
              <span className="w-2 h-2 bg-danger rounded-full animate-pulse"></span>
              <span>LIVE</span>
            </Link>

            <div className="hidden md:flex items-center space-x-5">
              {!user.isLoggedIn ? (
                <>
                  <button onClick={loginUser} className="text-[14px] font-medium text-white hover:text-rocket transition-colors">Sign In</button>
                  <button 
                    onClick={handleSubscribe}
                    className="bg-rocket hover:bg-rocket-hover text-white text-[14px] font-bold py-2 px-5 rounded animate-pulse-slow transition-all shadow-[0_0_15px_rgba(255,102,0,0.3)] hover:shadow-[0_0_25px_rgba(255,102,0,0.6)] transform hover:scale-105"
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <div className="flex items-center space-x-4">
                  {!user.isSubscribed && (
                    <button onClick={handleSubscribe} className="bg-rocket hover:bg-rocket-hover text-white text-[14px] font-bold py-2 px-5 rounded transition-all shadow-[0_0_15px_rgba(255,102,0,0.3)]">
                      Upgrade
                    </button>
                  )}
                  
                  {/* Profile Dropdown */}
                  <div className="group relative">
                    <div className="w-9 h-9 rounded bg-dark-card border border-white/10 flex items-center justify-center cursor-pointer hover:border-white/30 transition-colors">
                      <SafeIcon name="User" className="w-5 h-5 text-gray-300" />
                    </div>
                    
                    {/* Invisible hover bridge (pt-2) connecting icon to menu */}
                    <div className="absolute right-0 top-full pt-2 w-56 hidden group-hover:block z-50">
                      <div className="bg-dark-surface border border-white/10 rounded shadow-2xl py-2">
                        <div className="px-4 py-3 border-b border-white/10 mb-2">
                          <p className="text-sm font-bold text-white">Trader Account</p>
                          <p className="text-xs text-rocket mt-1">{user.isSubscribed ? 'Pro Member' : 'Free Tier'}</p>
                        </div>
                        <Link to="/admin" className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">Admin Panel</Link>
                        <button onClick={logoutUser} className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors">Sign out of RocketAlgo</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button 
              className="lg:hidden text-white p-2 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <SafeIcon name={mobileMenuOpen ? "X" : "Menu"} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 bg-dark-netflixDark pt-24 px-6 pb-6 flex flex-col lg:hidden"
          >
            <div className="flex flex-col space-y-6 flex-1">
              {navLinks.map((link) => (
                <Link 
                  key={link.name} 
                  to={link.path} 
                  className={`text-2xl font-semibold ${location.pathname === link.path ? 'text-rocket' : 'text-white'}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/live" className="text-2xl font-semibold text-danger flex items-center">
                <span className="w-3 h-3 bg-danger rounded-full animate-pulse mr-3"></span>
                Live Sessions
              </Link>
            </div>

            <div className="mt-auto pt-6 flex flex-col space-y-4 border-t border-white/10">
              {!user.isLoggedIn ? (
                <>
                  <button onClick={() => { loginUser(); setMobileMenuOpen(false); }} className="w-full py-4 text-white bg-white/5 rounded font-medium">Sign In</button>
                  <button onClick={handleSubscribe} className="w-full py-4 bg-rocket text-white rounded font-bold">Start Free Trial</button>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-3 p-4 bg-white/5 rounded">
                    <div className="w-12 h-12 rounded bg-dark-card flex items-center justify-center">
                      <SafeIcon name="User" className="w-6 h-6 text-gray-300" />
                    </div>
                    <div>
                      <p className="text-white font-bold">My Account</p>
                      <p className="text-sm text-rocket">{user.isSubscribed ? 'Pro Member' : 'Free Tier'}</p>
                    </div>
                  </div>
                  {!user.isSubscribed && (
                    <button onClick={handleSubscribe} className="w-full py-4 bg-rocket text-white rounded font-bold">Upgrade to Pro</button>
                  )}
                  <button onClick={() => { logoutUser(); setMobileMenuOpen(false); }} className="w-full py-4 text-gray-400 hover:text-white rounded font-medium">Sign Out</button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;